import React, {
  useState,
  useContext,
  useMemo,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';
import { Row, Col } from 'react-bootstrap';
import Joyride, { STATUS, ACTIONS, EVENTS } from 'react-joyride';

import NominationCardGrid from './NominationCardGrid';
import RankingService from '../../../services/RankingService';

import { CommonCollections } from '../../../App';
import { useStaticCollections } from '../../../context/StaticCollections';
import { useAdminCollections } from '../../../context/AdminCollections';
import { useLoggedInUserContextProvider } from '../../../context/LoggedInUserContext';
import { useAdminActionCollections } from '../../../context/AdminActionCollections';
import { ThemeContext } from '../../../context/ThemeContext';
import { calculateCommitteeMedian } from '../../../utils/candidateScoreCalculations';
import { getNominationRoundYear } from '../../../utils/getNominationRoundYear';
import { ELECTION_TYPE } from '../../../utils/constants';
import { devWarn, devError } from '../../../utils/devLog';

const TOUR_KEY = 'nomination-card-tour-completed';

const GridNominationCandidate = ({
  nominationsInSectionalCommittee,
  handleSelectClick,
  handleStage2SelectClick,
  handleCOIClick,
  onRankingSuccess,
  animatedNomination,
  shouldStartTour = false,
  allocatedNominationIds,
  isCouncilMemberOnly,
}) => {
  const [open, setOpen] = useState({});
  const [committeeRankings, setCommitteeRankings] = useState({});
  const [runTour, setRunTour] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [tourTargetCardId, setTourTargetCardId] = useState(null);
  const [tourHasActionButtons, setTourHasActionButtons] = useState(false);
  const { setIsTourOpen } = useContext(ThemeContext);

  const { userDetails } = useLoggedInUserContextProvider();
  const { enableCOI, enableCouncilMembers } = useAdminActionCollections();
  const { candidateScoresArray, committeeArray, coiDeclarationArray } =
    useAdminCollections();

  const isCouncilObserver = userDetails?.roles?.includes('council-observer');

  const isSCMember = userDetails?.roles?.includes('committee-member');
  const isSCChair = userDetails?.roles?.includes('committee-chair');
  const isAdmin =
    userDetails?.roles?.includes('super-admin') ||
    userDetails?.roles?.includes('admin');

  const userRoles = useMemo(
    () => ({
      isSCMember,
      isSCChair,
      isAdmin,
      isCouncilMember: userDetails?.roles?.includes('council-member'),
      isCouncilObserver,
    }),
    [isSCMember, isSCChair, isAdmin, userDetails?.roles, isCouncilObserver],
  );

  const currentRound = useMemo(() => getNominationRoundYear(new Date()), []);

  const userScoresMap = useMemo(() => {
    const map = {};
    if (!candidateScoresArray || !userDetails?.id) return map;

    candidateScoresArray.forEach((score) => {
      const nominationId = score.attributes?.nominationId || score.nominationId;
      const userId = score.attributes?.userId || score.userId;

      if (userId === userDetails.id) {
        map[nominationId] = score;
      }
    });

    return map;
  }, [candidateScoresArray, userDetails?.id]);

  const userCOIMap = useMemo(() => {
    const map = {};
    if (!coiDeclarationArray || !userDetails?.id) return map;

    coiDeclarationArray.forEach((coi) => {
      const nominationId = coi.attributes?.nominationId;
      const userId = coi.attributes?.userId;
      const round = coi.attributes?.nominationRound;

      if (userId === userDetails.id && round === currentRound) {
        map[nominationId] = coi;
      }
    });

    return map;
  }, [coiDeclarationArray, userDetails?.id, currentRound]);

  const getTourSteps = useCallback((cardId, hasActionButtons) => {
    const baseSteps = [
      {
        target: `#card-${cardId} .card-header-grid`,
        content:
          'This shows the candidate name. Click the eye icon to view nomination details or the people icon to see referee information.',
        disableBeacon: true,
        placement: 'bottom',
      },
      {
        target: `#card-${cardId} .vertical-stepper`,
        content: (
          <div className="tour-step-content">
            <p>
              Track nomination progress through three stages: Shortlisted,
              Recommended to Council, and Ranked.
            </p>
            <p>Outlined badges show completed stages</p>
            <p>Grey badges show incomplete stages</p>
          </div>
        ),
        placement: 'right',
      },
      {
        target: `#card-${cardId} .card-info-section`,
        content:
          "View the candidate's score information and committee statistics.",
        placement: 'top',
      },
    ];

    if (hasActionButtons) {
      baseSteps.push({
        target: `#card-${cardId} .mt-auto .d-flex`,
        content: 'Use these buttons to take actions on nominations.',
        placement: 'top',
      });
    }

    return baseSteps;
  }, []);

  const tourSteps = useMemo(
    () =>
      tourTargetCardId
        ? getTourSteps(tourTargetCardId, tourHasActionButtons)
        : [],
    [tourTargetCardId, tourHasActionButtons, getTourSteps],
  );

  const handleJoyrideCallback = (data) => {
    const { status, type, index, action } = data;

    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      setIsTourOpen(false);
      setStepIndex(0);
      setTourTargetCardId(null);
      setTourHasActionButtons(false);

      if (status === STATUS.FINISHED || status === STATUS.SKIPPED) {
        localStorage.setItem(TOUR_KEY, 'true');
      }
    } else if (type === EVENTS.STEP_AFTER) {
      const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
      setStepIndex(nextStepIndex);
    } else if (type === EVENTS.TARGET_NOT_FOUND) {
      devWarn(`Step ${index} target not found:`, tourSteps[index]?.target);
      setStepIndex(index + 1);
    }
  };

  const startTourForCard = (cardId, hasActionButtons) => {
    setIsTourOpen(true);
    setTourTargetCardId(cardId);
    setTourHasActionButtons(hasActionButtons);
    setStepIndex(0);
    setTimeout(() => setRunTour(true), 100);
  };

  const resetTour = () => {
    localStorage.removeItem(TOUR_KEY);
    setStepIndex(0);
    setRunTour(false);
    setTourTargetCardId(null);
    setTourHasActionButtons(false);
  };

  useEffect(() => {
    window.resetTour = resetTour;
    return () => {
      delete window.resetTour;
    };
  }, []);

  useEffect(() => {
    if (runTour) {
      document.body.style.overflow = 'hidden';

      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [runTour]);

  useEffect(() => {
    if (runTour && tourTargetCardId) {
      const handleCardClick = (e) => {
        const cardElement = document.getElementById(`card-${tourTargetCardId}`);
        if (cardElement && cardElement.contains(e.target)) {
          const isButton = e.target.closest('button');
          const isInteractiveElement = e.target.closest(
            'a, input, select, textarea',
          );

          if (isButton || isInteractiveElement) {
            setIsTourOpen(false);
            setRunTour(false);
            setStepIndex(0);
            setTourTargetCardId(null);
            setTourHasActionButtons(false);
          }
        }
      };

      document.addEventListener('click', handleCardClick, true);

      return () => {
        document.removeEventListener('click', handleCardClick, true);
      };
    }
  }, [runTour, tourTargetCardId]);

  const {
    fellows,
    declineReasons,
    refereeStatuses,
    sectionalCommittees,
    nonAasReferees,
  } = useStaticCollections();
  const {
    candidates,
    candidateArray,
    nominationArray,
    nominationStateArray,
    refereeReports,
  } = useAdminCollections();
  const { setNominationId } = useContext(CommonCollections);

  const isSCChairLocal = isSCChair;
  const isCouncilMember = userDetails?.roles?.includes('council-member');

  const showCOIFeatures =
    enableCOI &&
    (isSCChairLocal ||
      isSCMember ||
      isAdmin ||
      (enableCouncilMembers && isCouncilMember));

  const toggleCollapse = (nominationId) => {
    setOpen((prevOpen) => ({
      ...prevOpen,
      [nominationId]: !prevOpen[nominationId],
    }));
  };

  const getFullCandidateName = (candidateId) => {
    const candidate = candidateArray?.find((c) => c.id === candidateId);
    if (!candidate) return '';

    return `${candidate.attributes.name} ${candidate.attributes.lastName}`;
  };

  const sectionalCommitteeIds = useMemo(() => {
    const ids = new Set();
    nominationsInSectionalCommittee.forEach((nomination) => {
      if (
        nomination.attributes.electionTypeId === ELECTION_TYPE.SPECIAL_ELECTION
      ) {
        ids.add(15);
      } else if (
        nomination.attributes.electionTypeId ===
        ELECTION_TYPE.CORRESPONDING_MEMBER
      ) {
        ids.add(14);
      } else if (nomination.attributes.sectionalCommitteeId) {
        ids.add(nomination.attributes.sectionalCommitteeId);
      }
    });
    return Array.from(ids);
  }, [nominationsInSectionalCommittee]);

  const committeeMedians = useMemo(() => {
    const mediansMap = {};

    sectionalCommitteeIds.forEach((committeeId) => {
      const medianData = calculateCommitteeMedian(
        candidateScoresArray,
        committeeId,
      );
      mediansMap[committeeId] = medianData;
    });

    return mediansMap;
  }, [candidateScoresArray, sectionalCommitteeIds]);

  const fetchCommitteeRankings = useCallback(() => {
    const rankingsMap = {};

    sectionalCommitteeIds.forEach((committeeId) => {
      try {
        const rankings = RankingService.getRankingsByCommittee(
          committeeId,
          nominationArray,
          nominationStateArray,
        );
        rankingsMap[committeeId] = rankings;
      } catch (error) {
        devError(
          `Error fetching rankings for committee ${committeeId}:`,
          error,
        );
        rankingsMap[committeeId] = [];
      }
    });

    setCommitteeRankings(rankingsMap);
  }, [sectionalCommitteeIds, nominationArray, nominationStateArray]);

  useEffect(() => {
    if (sectionalCommitteeIds.length > 0) {
      fetchCommitteeRankings();
    }
  }, [fetchCommitteeRankings]);

  const handleRankingSuccess = useCallback(
    async (candidateName, rank, nominationId) => {
      if (onRankingSuccess) {
        onRankingSuccess(candidateName, rank, nominationId);
      }

      await fetchCommitteeRankings();
    },
    [onRankingSuccess, fetchCommitteeRankings],
  );

  const nominationsWithRankings = useMemo(
    () =>
      nominationsInSectionalCommittee.map((nomination) => {
        const nominationState = nominationStateArray?.find(
          (ns) => ns.attributes?.nominationId === nomination.id,
        );

        const ranking = nominationState?.attributes?.ranking;

        return {
          ...nomination,
          attributes: {
            ...nomination.attributes,
            ranking,
          },
        };
      }),
    [nominationsInSectionalCommittee, nominationStateArray],
  );

  const sortedNominations = useMemo(
    () =>
      [...nominationsWithRankings].sort((a, b) => {
        const nameA = getFullCandidateName(a.attributes.candidateId);
        const nameB = getFullCandidateName(b.attributes.candidateId);
        return nameA.localeCompare(nameB);
      }),
    [nominationsWithRankings],
  );

  useEffect(() => {
    const tourCompleted = localStorage.getItem(TOUR_KEY);

    if (shouldStartTour && sortedNominations.length > 0 && !tourCompleted) {
      const firstCardId = sortedNominations[0].id;

      startTourForCard(firstCardId, true);
    }
  }, [shouldStartTour, sortedNominations.length]);

  return (
    <>
      <Joyride
        steps={tourSteps}
        run={runTour}
        stepIndex={stepIndex}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        disableOverlayClose
        disableCloseOnEsc
        hideCloseButton
        hideBackButton={false}
        scrollToFirstStep
        scrollOffset={400}
        styles={{
          options: {
            arrowColor: '#fff',
            backgroundColor: '#fff',
            overlayColor: 'rgba(0, 0, 0, 0.4)',
            primaryColor: '#06b6d4',
            spotlightShadow: '0 0 15px rgba(0, 0, 0, 0.5)',
            textColor: '#333',
            zIndex: 10000,
          },
        }}
        locale={{
          back: 'Back',
          close: 'Close',
          last: 'Done',
          next: 'Next',
          skip: 'Skip Tour',
        }}
      />

      <Row className="g-5">
        {sortedNominations.map((nomination, index) => (
          <Col key={nomination.id} xs={12} md={6} lg={4}>
            <NominationCardGrid
              nomination={nomination}
              candidates={candidates}
              refereeReports={refereeReports}
              sectionalCommittees={sectionalCommittees}
              fellows={fellows}
              nonAasReferees={nonAasReferees}
              declineReasons={declineReasons}
              refereeStatuses={refereeStatuses}
              handleSelectClick={handleSelectClick}
              handleStage2SelectClick={handleStage2SelectClick}
              handleCOIClick={showCOIFeatures ? handleCOIClick : null}
              toggleCollapse={toggleCollapse}
              open={open}
              setNominationId={setNominationId}
              userDetails={userDetails}
              showCOIFeatures={showCOIFeatures}
              onRankingSuccess={handleRankingSuccess}
              committeeRankings={committeeRankings}
              committeeArray={committeeArray}
              coiDeclarationArray={coiDeclarationArray}
              animatedNomination={animatedNomination}
              isFirstCard={index === 0}
              onStartTour={(hasActionButtons) =>
                startTourForCard(nomination.id, hasActionButtons)
              }
              committeeMedians={committeeMedians}
              cardId={nomination.id}
              allocatedNominationIds={allocatedNominationIds}
              isCouncilMemberOnly={isCouncilMemberOnly}
              userScore={userScoresMap[nomination.id]}
              userCOI={userCOIMap[nomination.id]}
              userRoles={userRoles}
              currentRound={currentRound}
              enableCouncilMembers={enableCouncilMembers}
            />
          </Col>
        ))}
      </Row>
    </>
  );
};

GridNominationCandidate.propTypes = {
  nominationsInSectionalCommittee: PropTypes.array.isRequired,
  handleSelectClick: PropTypes.func.isRequired,
  handleStage2SelectClick: PropTypes.func,
  handleCOIClick: PropTypes.func,
  onRankingSuccess: PropTypes.func,
  animatedNomination: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    type: PropTypes.oneOf(['shortlisted', 'council-shortlisted', 'ranked']),
  }),
  shouldStartTour: PropTypes.bool,
  allocatedNominationIds: PropTypes.array,
  isCouncilMemberOnly: PropTypes.bool,
};

export default GridNominationCandidate;
